import React, { useState, useEffect } from 'react';
import { inboxFetch } from '../lib/inboxApi';
import { colors } from '../theme/tokens';

// Team-wide library of "candid responses" — pre-written messages any agent
// can insert into a chat from the reply-bar dropdown. Anyone on the team can
// add, edit or delete entries; the same list appears for everyone.
export default function InboxCandidResponses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit / create form state. When editingId === 'new' the form creates,
  // when it's a Mongo id the form updates that entry, when it's null the
  // form is hidden.
  const [editingId, setEditingId] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await inboxFetch('/api/candid-responses');
      if (!res.ok) throw new Error('load failed');
      setItems(await res.json());
    } catch {
      setError('Could not load candid responses.');
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setEditingId('new');
    setFormTitle('');
    setFormBody('');
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setFormTitle(item.title);
    setFormBody(item.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormTitle('');
    setFormBody('');
  };

  const handleSave = async () => {
    if (!formTitle.trim() || !formBody.trim()) {
      setError('Both a title and a message are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const isNew = editingId === 'new';
      const res = await inboxFetch(
        isNew ? '/api/candid-responses' : `/api/candid-responses/${editingId}`,
        {
          method: isNew ? 'POST' : 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: formTitle.trim(), body: formBody.trim() })
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Save failed.');
      }
      cancelEdit();
      await fetchList();
    } catch (err) {
      setError(err.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This affects everyone on the team.`)) return;
    setError(null);
    try {
      const res = await inboxFetch(`/api/candid-responses/${item._id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete failed');
      if (editingId === item._id) cancelEdit();
      await fetchList();
    } catch {
      setError('Could not delete that response.');
    }
  };

  if (loading) return <div style={s.loading}>Loading candid responses…</div>;

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <div>
          <h2 style={s.title}>Candid Responses</h2>
          <p style={s.description}>
            Shared team library of pre-written replies. Any agent can add or edit an
            entry — they all appear in the dropdown next to the reply button.
          </p>
        </div>
        {editingId === null && (
          <button style={s.newBtn} onClick={startNew}>+ New response</button>
        )}
      </div>

      {error && <div style={s.error}>{error}</div>}

      {editingId !== null && (
        <div style={s.editCard}>
          <div style={s.editHeader}>
            {editingId === 'new' ? 'New candid response' : 'Editing response'}
          </div>
          <label style={s.fieldLabel}>Title (shown in dropdown)</label>
          <input
            type="text"
            style={s.input}
            value={formTitle}
            maxLength={80}
            placeholder="e.g. Delivery times"
            onChange={e => setFormTitle(e.target.value)}
          />
          <label style={{ ...s.fieldLabel, marginTop: '12px' }}>Message body</label>
          <textarea
            style={s.textarea}
            rows={5}
            value={formBody}
            maxLength={4000}
            placeholder="The message that gets dropped into the chat when this is picked…"
            onChange={e => setFormBody(e.target.value)}
          />
          <div style={s.charCount}>{formBody.length} / 4000</div>
          <div style={s.editActions}>
            <button
              style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : editingId === 'new' ? 'Create response' : 'Save changes'}
            </button>
            <button style={s.cancelBtn} onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {items.length === 0 && editingId === null ? (
        <div style={s.emptyState}>
          No candid responses yet. Click <strong>+ New response</strong> to add the first one.
        </div>
      ) : (
        <div style={s.list}>
          {items.map(item => (
            <div key={item._id} style={s.itemCard}>
              <div style={s.itemHeader}>
                <span style={s.itemTitle}>{item.title}</span>
                <div style={s.itemActions}>
                  <button style={s.linkBtn} onClick={() => startEdit(item)}>Edit</button>
                  <button
                    style={{ ...s.linkBtn, color: '#f3b0aa' }}
                    onClick={() => handleDelete(item)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div style={s.itemBody}>{item.body}</div>
              {(item.createdBy || item.updatedBy) && (
                <div style={s.itemMeta}>
                  {item.updatedBy
                    ? `Last edited by ${item.updatedBy}`
                    : `Created by ${item.createdBy}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  container: { padding: '24px', maxWidth: '640px', overflowY: 'auto' },
  loading: { padding: '40px', color: colors.textMuted, textAlign: 'center', fontSize: '14px' },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '16px'
  },
  title: { margin: '0 0 6px', fontSize: '20px', fontWeight: '700', color: colors.text },
  description: { fontSize: '13px', color: colors.textMuted, lineHeight: '1.6', margin: 0 },
  newBtn: {
    flexShrink: 0,
    background: colors.parGreen,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  error: {
    background: 'rgba(229,84,75,0.14)',
    color: '#f3b0aa',
    border: '1px solid rgba(229,84,75,0.35)',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '16px'
  },
  editCard: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '18px'
  },
  editHeader: {
    fontSize: '13px',
    fontWeight: '700',
    color: colors.parGreenBright,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '12px'
  },
  fieldLabel: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '5px'
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    outline: 'none',
    boxSizing: 'border-box',
    background: colors.inputBg,
    color: colors.text
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'inherit',
    lineHeight: '1.5',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
    background: colors.inputBg,
    color: colors.text
  },
  charCount: {
    fontSize: '11px',
    color: colors.textFaint,
    textAlign: 'right',
    marginTop: '3px'
  },
  editActions: {
    marginTop: '14px',
    display: 'flex',
    gap: '10px'
  },
  saveBtn: {
    background: colors.parGreen,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveBtnDisabled: {
    background: colors.surface2,
    color: colors.textFaint,
    cursor: 'not-allowed'
  },
  cancelBtn: {
    background: 'transparent',
    color: colors.textMuted,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '28px',
    textAlign: 'center',
    background: colors.glass,
    border: `1px dashed ${colors.glassBorder}`,
    borderRadius: '10px',
    color: colors.textMuted,
    fontSize: '13px'
  },
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  itemCard: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '10px',
    padding: '14px 16px'
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '6px'
  },
  itemTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: colors.text
  },
  itemActions: { display: 'flex', gap: '10px' },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    color: colors.parGreenBright,
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '2px 4px'
  },
  itemBody: {
    fontSize: '13px',
    color: colors.text,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  itemMeta: {
    marginTop: '8px',
    fontSize: '11px',
    color: colors.textFaint,
    fontStyle: 'italic'
  }
};
